<template>
    <CategoriesList
        v-if="!showCategoryDetails && !showLinkDetails"
        @editCategory="editCategory($event)"
        @addCategory="createCategory"
        @editLink="editLink($event)"
        @addLink="createLink($event)"
    />
    <CategoryDetails
        v-if="showCategoryDetails"
        :category="categoryToEdit"
        :backupData="backupData"
        :mode="mode"
        @cancel="cancelCategory"
        @save="saveCategory"
    />
    <LinkDetails
        v-if="showLinkDetails"
        :link="linkToEdit"
        :backupData="backupData"
        :mode="mode"
        @cancel="cancelLink"
        @save="saveLink"
    />
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";
import CategoriesList from "./navigation/CategoriesList.vue";
import CategoryDetails from "./navigation/CategoryDetails.vue";
import LinkDetails from "./navigation/LinkDetails.vue";
import { useBackupData } from "@/shared/utils/forms/useBackupData";
import { useCleanQueryParams } from "@/shared/utils/router/useCleanQueryParams";

const EMPTY_LINK = (category) => ({
    name: "",
    url: "",
    categoryId: category.id,
});

const MODE = {
    create: "create",
    edit: "edit",
};

export default {
    components: {
        CategoriesList,
        CategoryDetails,
        LinkDetails,
    },
    setup() {
        const { cleanQueryParams } = useCleanQueryParams();
        const { backupData, setBackupValue } = useBackupData({});
        return { backupData, setBackupValue, cleanQueryParams };
    },
    computed: {
        ...mapState("legal", ["footer", "categories"]),
    },
    data() {
        return {
            showCategoryDetails: false,
            showLinkDetails: false,
            categoryToEdit: {},
            linkToEdit: {},
            mode: MODE.create,
        };
    },
    mounted() {
        this.$watch(() => this.$route.query.open, this.handleQueryChange, {
            immediate: true,
        });
    },
    methods: {
        ...mapMutations("legal", [
            "addLink",
            "addCategory",
            "changeCategory",
            "removeCategory",
            "changeLink",
        ]),
        ...mapActions("legal", ["deleteLink"]),
        ...mapActions("legal/projects", ["saveSnapshot"]),
        ...mapActions("alert", ["showWarnMessage"]),
        editCategory(category) {
            this.mode = MODE.edit;
            this.categoryToEdit = category;
            this.setBackupValue(this.categoryToEdit);
            this.showCategoryDetails = true;
        },
        createCategory() {
            this.mode = MODE.create;
            this.addCategory();
            this.categoryToEdit = this.categories[this.categories.length - 1];
            this.setBackupValue(this.categoryToEdit);
            this.showCategoryDetails = true;
        },
        saveCategory() {
            if (!this.categoryToEdit._created && !this.categoryToEdit._deleted) {
                this.categoryToEdit._changed = true;
            }
            this.saveSnapshot();
            this.showCategoryDetails = false;
        },
        async cancelCategory() {
            if (this.mode === MODE.edit) {
                this.changeCategory(this.backupData);
            } else {
                this.removeCategory(this.categoryToEdit);
            }
            await this.saveSnapshot();
            this.showCategoryDetails = false;
        },
        editLink(link) {
            this.mode = MODE.edit;
            this.linkToEdit = link;
            this.setBackupValue(this.linkToEdit);
            this.showLinkDetails = true;
        },
        createLink(category) {
            this.mode = MODE.create;
            this.linkToEdit = EMPTY_LINK(category ? category : this.categories[0]);
            this.setBackupValue(this.linkToEdit);
            this.addLink(this.linkToEdit);
            this.showLinkDetails = true;
        },
        saveLink() {
            if (!this.linkToEdit._created && !this.linkToEdit._deleted) {
                this.linkToEdit._changed = true;
            }
            this.saveSnapshot();
            this.showLinkDetails = false;
        },
        async cancelLink() {
            if (this.mode === MODE.edit) {
                this.changeLink(this.backupData);
            } else {
                this.deleteLink(this.linkToEdit);
            }
            await this.saveSnapshot();
            this.showLinkDetails = false;
        },
        handleQueryChange() {
            if (this.$route.query.open === "edit-category") {
                const categoryId = +this.$route.query.id;
                setTimeout(() => {
                    this.cleanQueryParams(["open", "id"]);
                }, 100);
                const category = this.categories.find((c) => c.id === categoryId);

                if (!category) {
                    return;
                }
                if (this.showCategoryDetails && this.categoryToEdit.id !== categoryId) {
                    this.showWarnMessage({
                        message: this.$t("legal.navigation.unsaved_changes_category"),
                    });

                    return;
                }
                if (this.showLinkDetails) {
                    this.showWarnMessage({
                        message: this.$t("legal.navigation.unsaved_changes_link"),
                    });

                    return;
                }
                this.editCategory(category);
            }
            if (this.$route.query.open === "edit-link") {
                const linkId = +this.$route.query.id;
                setTimeout(() => {
                    this.cleanQueryParams(["open", "id"]);
                }, 100);
                const link = this.categories.flatMap((s) => s.links).find((q) => q.id === linkId);

                if (!link) {
                    return;
                }
                if (this.showLinkDetails && this.linkToEdit.id !== linkId) {
                    this.showWarnMessage({
                        message: this.$t("legal.navigation.unsaved_changes_link"),
                    });

                    return;
                }
                if (this.showCategoryDetails) {
                    this.showWarnMessage({
                        message: this.$t("legal.navigation.unsaved_changes_category"),
                    });

                    return;
                }

                this.editLink(link);
            }
        },
    },
};
</script>
